import { QueryClient, QueryFunction } from "@tanstack/react-query";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const res = await fetch(url, {
    method,
    headers: data ? { "Content-Type": "application/json" } : {},
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  if (res.status === 401) {
    // Mutations/commands also detect an expired session here: flip the cached
    // admin auth state to logged-out so the AdminLayout guard redirects to
    // /admin/login. This matters because the leads page has no polling query
    // that would otherwise surface the expiry — without this, the operator's
    // first action after timeout would just show a raw 401 toast and leave
    // them stranded. Harmless on the login page, where /api/admin/me is
    // already null and no admin guard is mounted.
    queryClient.setQueryData(["/api/admin/me"], null);
  }

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const res = await fetch(queryKey.join("/") as string, {
      credentials: "include",
    });

    if (res.status === 401) {
      if (unauthorizedBehavior === "returnNull") {
        return null;
      }
      // on401 === "throw": an authenticated request hit an expired/invalid
      // session. Flip the cached admin auth state to "logged out" so the
      // admin route guard (AdminLayout) redirects to /admin/login instead of
      // leaving the operator staring at empty lists. We still throw below so
      // the calling query resolves to an error state (handled per-page)
      // rather than silently returning no data.
      queryClient.setQueryData(["/api/admin/me"], null);
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});

// The global defaults above (staleTime: Infinity, refetchOnWindowFocus: false)
// are tuned for the prerendered marketing pages, where content is static and
// re-fetching on every focus would waste requests. They are wrong for the
// admin CRM: with data never going stale, new leads (and other operator data)
// never appear without a hard page reload.
//
// Spread `adminQueryOptions` into admin `useQuery` calls to opt those queries
// into a short stale window plus refetch on focus/reconnect, so the panel
// stays fresh without touching the public pages' aggressive caching.
export const adminQueryOptions = {
  staleTime: 30_000,
  refetchOnWindowFocus: true,
  refetchOnReconnect: true,
} as const;
